import React from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Settings,
  Dashboard,
  Group,
  SwitchAccessShortcut,
  Business,
  Download,
  Security,
} from "@mui/icons-material";
import theme from "../../theme";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "../../store";
import { getCompany } from "../../slices/company";
import Company from "../../types/company";
import checkRole, { UserRoles } from "../../helpers/checkUserRoles";
import CompanyLogo from "../base/CompanyLogo";

interface Props {
  handleClose: () => void;
}

const MenuView: React.FC<Props> = (props) => {
  const { handleClose } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedCompany, allowedCompanies } = useSelector(
    (store) => store.company
  );
  const { dbUser } = useSelector((store) => store.auth);
  const { ssoStatus } = useSelector((store) => store.provider);

  const handleChangeCompany = (selected?: Company) => {
    dispatch(getCompany(selected?.id || undefined));
  };

  const getCompanyButton = () => {
    if (allowedCompanies.length! > 1 && !ssoStatus?.useSso) {
      if (!!selectedCompany) {
        return {
          label: "Change Company",
          icon: <SwitchAccessShortcut />,
          function: () => handleChangeCompany(),
          enabled: true,
          hidden: false,
        };
      } else {
        return {
          label: "Select Company",
          icon: <Business />,
          function: () => {
            navigate("/select");
          },
          enabled: true,
          hidden: false,
        };
      }
    }

    if (allowedCompanies.length! === 1 && !selectedCompany) {
      let myCompany = allowedCompanies[0];
      return {
        label: `Return to ${myCompany?.settings?.companyName}`,
        icon: <CompanyLogo company={myCompany} size="40px" />,
        function: () => {
          handleChangeCompany(myCompany);
          navigate("/apps");
        },
        enabled: true,
        hidden: false,
      };
    }

    return {
      label: "",
      icon: <Dashboard />,
      function: () => {},
      enabled: false,
      hidden: true,
    };
  };

  const menuItems = [
    {
      label: "Apps",
      icon: <Dashboard />,
      function: () => navigate(`/apps`),
      enabled: !!selectedCompany?.finishedSetup,
      hidden: false,
    },
    {
      label: "Users",
      icon: <Group />,
      function: () => navigate("/users"),
      enabled: !!selectedCompany?.finishedSetup,
      hidden: !checkRole(dbUser!, UserRoles.AnyAdmin),
    },
    // {
    //   label: "Download",
    //   icon: <Download />,
    //   function: () => navigate("/download"),
    //   enabled: !!selectedCompany?.finishedSetup,
    //   hidden: !checkRole(dbUser!, UserRoles.AnyAdmin),
    // },
    // {
    //   label: "Security Hubs",
    //   icon: <Security />,
    //   function: () => navigate("/security"),
    //   enabled: !!selectedCompany?.finishedSetup,
    //   hidden: !checkRole(dbUser!, UserRoles.AnyAdmin),
    // },
    {
      label: !!selectedCompany?.finishedSetup ? "Settings" : "Setup",
      icon: <Settings />,
      function: !!selectedCompany?.finishedSetup
        ? () => navigate("/settings")
        : () => navigate("/setup"),
      enabled: true,
      hidden: !checkRole(dbUser!, UserRoles.GlobalAdmin, true),
    },
  ];

  if (allowedCompanies.length! > 0) {
    const companySelection = getCompanyButton();

    menuItems.push(companySelection!);
  }

  return (
    <Box width={250} display="flex" role="presentation" onClick={handleClose}>
      <List sx={{ width: "100%" }}>
        {!!selectedCompany && selectedCompany.finishedSetup ? (
          <>
            <Box>
              <ListItem>
                <ListItemIcon sx={{ paddingX: 2 }}>
                  <CompanyLogo company={selectedCompany} size="50px" />
                </ListItemIcon>
                <ListItemText
                  primary={selectedCompany.settings?.companyName}
                  slotProps={{
                    primary: {
                      fontSize: 16,
                      fontWeight: "bold",
                      color: "primary",
                    },
                  }}
                />
              </ListItem>
            </Box>
            <Divider sx={{ margin: 1 }} />
          </>
        ) : (
          <ListItem>
            <ListItemText
              primary={
                allowedCompanies.length! > 1 && !selectedCompany
                  ? "Select Company to Access Apps"
                  : "Save Company to Access Apps"
              }
              primaryTypographyProps={{
                fontSize: 16,
                color: "primary",
                textAlign: "center",
              }}
            />
          </ListItem>
        )}
        {menuItems
          .filter((x) => !x.hidden)
          .map((item) => (
            <Box key={item.label}>
              <ListItem disablePadding>
                <ListItemButton
                  disabled={!item.enabled}
                  onClick={item.function}
                  sx={{
                    "&:hover, &:focus": {
                      "& svg:first-of-type": {
                        transition: "0.5s",
                        transform: "rotate(-180deg)",
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ paddingX: 2 }}>
                    <Avatar
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        width: "35px",
                        height: "35px",
                        color: "#fff",
                      }}
                    >
                      <IconButton color="inherit">{item.icon}</IconButton>
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: "light",
                      color: "primary",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </Box>
          ))}
      </List>
    </Box>
  );
};

export default MenuView;
